import React, { useEffect, useState } from 'react';
import { Clock, User, FileText, Trash2, Edit, ToggleRight, X } from 'lucide-react';
import Modal from './common/Modal';
import Loading from './common/Loading';
import Alert from './common/Alert';
import Badge from './common/Badge';
import { PlanLog } from '../types';
import { planService } from '../services/planService';
import { formatDateTime, formatCurrency } from '../utils/formatters';

interface PlanHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  planId: number;
  planName: string;
}

const PlanHistoryModal: React.FC<PlanHistoryModalProps> = ({
  isOpen,
  onClose,
  planId,
  planName,
}) => {
  const [logs, setLogs] = useState<PlanLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && planId) {
      loadLogs();
    }
  }, [isOpen, planId]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await planService.getLogs(planId);
      setLogs(data);
    } catch (err: any) {
      setError('Erro ao carregar histórico');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'created':
        return <FileText className="w-5 h-5 text-green-500" />;
      case 'updated':
        return <Edit className="w-5 h-5 text-blue-500" />;
      case 'deleted':
        return <Trash2 className="w-5 h-5 text-red-500" />;
      case 'status_changed':
        return <ToggleRight className="w-5 h-5 text-orange-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'created':
        return 'Criado';
      case 'updated':
        return 'Atualizado';
      case 'deleted':
        return 'Excluído';
      case 'status_changed':
        return 'Status Alterado';
      default:
        return action;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'created':
        return 'success';
      case 'updated':
        return 'primary';
      case 'deleted':
        return 'danger';
      case 'status_changed':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const renderChanges = (log: PlanLog) => {
    if (log.action === 'created' && log.new_data) {
      return (
        <div className="mt-2 space-y-1 text-sm">
          <div className="text-gray-700">
            <span className="font-medium">Nome:</span> {log.new_data.nome}
          </div>
          {log.new_data.descricao && (
            <div className="text-gray-700">
              <span className="font-medium">Descrição:</span> {log.new_data.descricao}
            </div>
          )}
          <div className="text-gray-700">
            <span className="font-medium">Valor:</span> {formatCurrency(log.new_data.valor)}
          </div>
          <div className="text-gray-700">
            <span className="font-medium">Status:</span>{' '}
            <Badge variant={log.new_data.status === 'ativo' ? 'success' : 'secondary'}>
              {log.new_data.status === 'ativo' ? 'Ativo' : 'Inativo'}
            </Badge>
          </div>
        </div>
      );
    }

    if (log.action === 'status_changed' && log.previous_data && log.new_data) {
      return (
        <div className="mt-2 text-sm">
          <div className="flex items-center gap-2">
            <Badge variant={log.previous_data.status === 'ativo' ? 'success' : 'secondary'}>
              {log.previous_data.status === 'ativo' ? 'Ativo' : 'Inativo'}
            </Badge>
            <span className="text-gray-500">→</span>
            <Badge variant={log.new_data.status === 'ativo' ? 'success' : 'secondary'}>
              {log.new_data.status === 'ativo' ? 'Ativo' : 'Inativo'}
            </Badge>
          </div>
        </div>
      );
    }

    if (log.action === 'updated' && log.previous_data && log.new_data) {
      const changes: string[] = [];

      if (log.previous_data.nome !== log.new_data.nome) {
        changes.push(`Nome: "${log.previous_data.nome}" → "${log.new_data.nome}"`);
      }

      if (log.previous_data.descricao !== log.new_data.descricao) {
        changes.push(
          `Descrição: "${log.previous_data.descricao || 'Vazio'}" → "${log.new_data.descricao || 'Vazio'}"`
        );
      }

      if (log.previous_data.valor !== log.new_data.valor) {
        changes.push(
          `Valor: ${formatCurrency(log.previous_data.valor)} → ${formatCurrency(log.new_data.valor)}`
        );
      }

      if (log.previous_data.status !== log.new_data.status) {
        changes.push(
          `Status: ${log.previous_data.status} → ${log.new_data.status}`
        );
      }

      return (
        <div className="mt-2 space-y-1 text-sm text-gray-700">
          {changes.map((change, index) => (
            <div key={index} className="pl-2 border-l-2 border-gray-300">
              {change}
            </div>
          ))}
        </div>
      );
    }

    if (log.action === 'deleted' && log.previous_data) {
      return (
        <div className="mt-2 space-y-1 text-sm text-red-600">
          <div>
            <span className="font-medium">Nome:</span> {log.previous_data.nome}
          </div>
          <div>
            <span className="font-medium">Valor:</span> {formatCurrency(log.previous_data.valor)}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Histórico: ${planName}`} size="lg">
      <div className="space-y-4">
        {error && (
          <Alert variant="error" onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading ? (
          <div className="flex justify-center py-8">
            <Loading />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Clock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>Nenhum registro de histórico encontrado</p>
          </div>
        ) : (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {logs.map((log) => (
              <div
                key={log.id}
                className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-gray-300 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className="mt-1">{getActionIcon(log.action)}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant={getActionColor(log.action) as any}>
                        {getActionLabel(log.action)}
                      </Badge>
                      <span className="text-sm text-gray-500">
                        {formatDateTime(log.created_at)}
                      </span>
                    </div>

                    {renderChanges(log)}

                    {log.user && (
                      <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                        <User className="w-4 h-4" />
                        <span>
                          {log.user.name} ({log.user.email})
                        </span>
                      </div>
                    )}

                    {log.notes && (
                      <div className="mt-2 text-sm text-gray-600 italic">
                        {log.notes}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
          >
            <X className="w-4 h-4" />
            Fechar
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default PlanHistoryModal;
